import { I18n } from "@/i18n/I18n";
import { FileSubjectTypes } from "@/types/fileTypes";
import { useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import { useGlobalStore } from "@/frontend/store/GlobalStore";
import { FusawarePermission } from "@/frontend/fusawareUser";
import { FileDragAndDrop } from "../files/FileDragAndDrop";

export function CreateHierarchyModelButton(props: { 
  createModel: (title: string, description: string, parentId: string) => Promise<string | undefined>, 
  modelLabel: string, 
  parentLabel?: string,
  refetchSubject: (id: string) => Promise<void>, 
  subjectType: FileSubjectTypes, 
  parents?: { id: string, title: string }[],
  defaultParentId?: string,
 }) {
  const { createModel, modelLabel, parentLabel, refetchSubject, subjectType, parents, defaultParentId } = props;
  const userClass = useGlobalStore(state => state.userClass);
  const [showCreateModal, setShowCreateModal] = useState(false);

  if(!userClass?.fusaHasPermission(FusawarePermission.EDIT_HIERARCHY)) {
    return null;
  }
  
  return (
    <>
      <Button className="btn btn-primary btn-sm" onClick={() => setShowCreateModal(true)}>
        {I18n.get('createModel', { model: modelLabel })}
      </Button>
      {showCreateModal && <CreateHierarchyModal
        modelLabel={modelLabel}
        defaultParentId={defaultParentId}
        setShowModal={setShowCreateModal}
        createModel={createModel}
        subjectType={subjectType}
        parents={parents}
        parentLabel={parentLabel}
        refetchSubject={refetchSubject}
      />}
    </>
  );
}

export function CreateHierarchyModal(props: { 
  modelLabel: string, 
  defaultParentId?: string, 
  setShowModal: (showModal: boolean) => void,
  createModel: (title: string, description: string, parentId: string) => Promise<string | undefined>,
  subjectType: FileSubjectTypes,
  parents?: { id: string, title: string }[],
  parentLabel?: string,
  refetchSubject: (id: string) => Promise<void>,
 }) {
  const { modelLabel, defaultParentId, setShowModal, parents, parentLabel, refetchSubject, createModel, subjectType } = props;

  const createFile = useGlobalStore(state => state.createFile);
  const [title, setTitle] = useState('');
  const [titleError, setTitleError] = useState('');
  const [description, setDescription] = useState('');
  const [file, setFile] = useState<File | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number>(0);
  const [parentId, setParentId] = useState(defaultParentId || '');
  const [parentError, setParentError] = useState('');

  const handleClose = () => {
    setTitle('');
    setDescription('');
    setTitleError('');
    setParentId('');
    setParentError('');
    setShowModal(false);
  };

  const handleTitleChange = (value: string) => {
    setTitle(value);
    setTitleError('');
  };

  const handleSubmit = async () => {
    if(!title) {
      setTitleError(I18n.get('requiredFieldError', { field: I18n.get('title') }));
      return;
    }

    if(parents && !parentId) {
      setParentError(I18n.get('requiredFieldError', { field: parentLabel || I18n.get('parent') }));
      return undefined;
    }

    setIsSubmitting(true);
    const newId = await createModel(title, description, parentId);
    if (file && newId) {
      await createFile(
        { fileName: file.name, subjectId: newId, subjectType },
        file,
        () => void refetchSubject(newId),
        setUploadProgress
      );
    }
    setUploadProgress(0);
    setIsSubmitting(false);
    if (newId) {
      setFile(undefined);
      handleClose();
    }
  };

  const handleParentChange = (value: string) => {
    setParentId(value);
    setParentError('');
  };

  return (
    <Modal show onHide={handleClose}>
      <Modal.Header closeButton>
        <Modal.Title>{I18n.get('createModel', { model: modelLabel })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Label>{I18n.get('title')}</Form.Label>
          <Form.Control
            type="text"
            value={title}
            onChange={(e) => handleTitleChange(e.target.value)}
            placeholder={I18n.get('enterField', { field: I18n.get('title') })}
            aria-label={I18n.get('enterField', { field: I18n.get('title') })}
            isInvalid={!!titleError}
          />
          <Form.Control.Feedback type="invalid">
            {titleError}
          </Form.Control.Feedback>
          <br />
          <Form.Label>{I18n.get('description')}</Form.Label>
          <Form.Control
            as="textarea"
            rows={3}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder={I18n.get('enterField', { field: I18n.get('description') })}
            aria-label={I18n.get('enterField', { field: I18n.get('description') })}
          />
        { parents && !defaultParentId && <>
          <br />
          <Form.Label>{parentLabel}</Form.Label>
          <Form.Select
            value={parentId}
            onChange={(e) => handleParentChange(e.target.value)}
            isInvalid={!!parentError}
            aria-label={parentLabel}
          >
            <option value="">{I18n.get('selectParent', { parentLabel: parentLabel || '' })}</option>
            {parents.map((parent) => (
              <option key={parent.id} value={parent.id}>{parent.title}</option>
            ))}
          </Form.Select>
          <Form.Control.Feedback type="invalid">
            {parentError}
          </Form.Control.Feedback>
          </> }
          <FileDragAndDrop file={file} setFile={setFile} accept="image/*" uploading={isSubmitting} uploadProgress={uploadProgress} />
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={isSubmitting}>
          {I18n.get('cancel')}
        </Button>
        <Button variant="primary" onClick={() => void handleSubmit()} disabled={isSubmitting}>
          {isSubmitting ? I18n.get('saving') : I18n.get('create')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
